import { useMemo } from 'react'
import { addDays, dayKey, densityLevel, formatTime, meetingsOnDay, type CalendarViewKind } from '../../lib/calendar'
import { externalContacts, isThreat, kindOf, threatLabel } from '../../lib/meetingCallouts'
import type { MeetingView } from '../../types/calendar'

// The window at a glance: how many rooms, how many with clients, which accounts, what Scout has flagged,
// the next client meetings with prep one press away, and the days as a density strip you can jump into.

const DOW = new Intl.DateTimeFormat('en-US', { weekday: 'short' })
const MD = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })

export function Glance({ view, from, to, meetings, now, onPick, onPrep, onOpenDay }: {
  view: CalendarViewKind; from: Date; to: Date; meetings: MeetingView[]; now: Date
  onPick: (m: MeetingView) => void; onPrep: (m: MeetingView) => void; onOpenDay: (day: Date) => void
}) {
  const live = meetings.filter((m) => !m.meeting.isCancelled)
  const threats = live.filter(isThreat)
  const clients = live.filter((m) => kindOf(m) === 'client')
  const accounts = new Set(clients.map((m) => m.meeting.companyId).filter(Boolean)).size
  const firsts = live.filter((m) => m.facts?.firstWithAccount).length
  const decision = live.filter((m) => m.facts?.decisionMakerInRoom).length
  const resurfacing = live.filter((m) => (m.facts?.resurfacingContactIds.length ?? 0) > 0).length
  const transcripts = live.filter((m) => m.transcriptId).length
  const tactics = live.reduce((n, m) => n + (m.facts?.tacticsDue.length ?? 0), 0)
  const nextUp = clients.filter((m) => new Date(m.meeting.start) >= now).sort((a, b) => new Date(a.meeting.start).getTime() - new Date(b.meeting.start).getTime()).slice(0, 4)

  const strip = useMemo(() => {
    const days: Date[] = []
    for (let d = from; d < to; d = addDays(d, 1)) days.push(d)
    const items = live.map((m) => ({ start: m.meeting.start, end: m.meeting.end, client: kindOf(m) === 'client' }))
    const counts = days.map((d) => { const on = meetingsOnDay(items, d); return { day: d, total: on.length, client: on.filter((x) => x.client).length } })
    const max = Math.max(0, ...counts.map((c) => c.total))
    return counts.map((c) => ({ ...c, level: densityLevel(c.total, max) }))
  }, [from.getTime(), to.getTime(), live])

  return (
    <section className="cal-glance">
      {threats.length > 0 && (
        <div className="cal-glance-threats" role="alert">
          <p className="kicker">{threats.length === 1 ? 'A competitor is in one of these rooms' : `Competitors are in ${threats.length} of these rooms`}</p>
          <ul>
            {threats.slice(0, 4).map((m) => (
              <li key={m.meeting.meetingId}>
                <button className="link-button" onClick={() => onPick(m)}>
                  <span className="threat-mark" aria-hidden="true">!</span>
                  <span>{threatLabel(m.facts!.threat!)}</span>
                  <span className="muted small">{m.companyName ? `${m.companyName} · ` : ''}{DOW.format(new Date(m.meeting.start))} {MD.format(new Date(m.meeting.start))} · {m.meeting.subject || '(no subject)'}</span>
                </button>
              </li>
            ))}
            {threats.length > 4 && <li className="muted small">And {threats.length - 4} more in this window.</li>}
          </ul>
        </div>
      )}

      <div className="cal-glance-stats">
        <div className="glance-stat"><strong>{live.length}</strong><span>meetings this {view === 'month' ? 'month' : view === 'week' ? 'week' : 'page'}</span></div>
        <div className="glance-stat"><strong>{clients.length}</strong><span>with clients · {accounts} account{accounts === 1 ? '' : 's'}</span></div>
        <div className="glance-stat"><strong>{firsts}</strong><span>first meetings with an account</span></div>
        <div className="glance-stat"><strong>{decision}</strong><span>with a decision maker</span></div>
        <div className="glance-stat"><strong>{resurfacing}</strong><span>quiet contacts resurfacing</span></div>
        <div className="glance-stat"><strong>{transcripts}</strong><span>transcripts linked · {tactics} tactic{tactics === 1 ? '' : 's'} due</span></div>
      </div>

      <div className="cal-glance-next">
        <p className="kicker">Next up with clients</p>
        {nextUp.length === 0 ? <p className="cal-glance-quiet">No client meetings ahead in this window.</p> : (
          <ul>
            {nextUp.map((m) => {
              const who = m.companyName ?? externalContacts(m)[0]?.name ?? 'a contact'
              return (
                <li key={m.meeting.meetingId}>
                  <button className="link-button cal-glance-item" onClick={() => onPick(m)}>
                    <span className="cal-glance-when">{DOW.format(new Date(m.meeting.start))} {MD.format(new Date(m.meeting.start))} · {formatTime(m.meeting.start)}</span>
                    <span className="cal-glance-subject">{m.meeting.subject || '(no subject)'}</span>
                    <span className="cal-glance-who">{who}{m.facts?.decisionMakerInRoom ? ' · decision maker' : ''}{m.facts?.firstWithAccount ? ' · first meeting' : ''}</span>
                  </button>
                  {m.meeting.companyId && <button className="cal-glance-prep" onClick={() => onPrep(m)}>Prep</button>}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className="cal-glance-strip" aria-label="Meetings per day">
        <p className="kicker">Days in this window</p>
        <div className="cal-strip">
          {strip.map((c) => (
            <button key={dayKey(c.day)} className={`cal-strip-day heat-${c.level} ${dayKey(c.day) === dayKey(now) ? 'today' : ''}`} onClick={() => onOpenDay(c.day)} title={`${MD.format(c.day)}: ${c.total} meeting${c.total === 1 ? '' : 's'}${c.client ? `, ${c.client} with clients` : ''}`}>
              <span className="cal-strip-bar" style={{ height: `${c.level * 25}%` }}><span className="cal-strip-client" style={{ height: c.total ? `${(c.client / c.total) * 100}%` : 0 }} /></span>
              <span className="cal-strip-label">{c.day.getDate()}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  )
}
